import { useEffect, useState } from 'react';
import { createBuildings, deleteBuilding, updateBuilding } from '../lib/api';
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";
import Label from './Common/Label';

export default function BuildingCard({
    setShowBuildingModal,
    allProps,
    onBuildingCreated,
    propertyName,
    setBuildingOptions,
    onActionSuccess,
    setAllProps,
    fetchAllProperty,
    buildingOptions,
    isBuildingDisabled,
    setIsBuildingDisabled
}) {
    const isActionsDisabled = useIsWriteRestrictedUser();
    const [buildingInput, setBuildingInput] = useState('');
    const [buildings, setBuildings] = useState([]);
    const [editingBuildingUnid, setEditingBuildingUnid] = useState(null);
    const [errors, setErrors] = useState({});

    const handleBuildingSave = () => {
        setShowBuildingModal(false);
    }

    const PropertyName = propertyName;

    const selectedProperty = allProps?.properties?.find(
        (prop) => prop.name === propertyName
    );


    useEffect(() => {
        if (selectedProperty?.buildings?.length) {
            setBuildings(selectedProperty.buildings);
        }
    }, [selectedProperty]);

    useEffect(() => {
        const names = buildings.map((b) => b.name);
        setBuildingOptions(names);
    }, [buildings, setBuildingOptions]);

    const validate = () => {
        const newErrors = {};
        if (!buildingInput) {
            newErrors.buildingName = 'Enter Building Name';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddBuilding = async (e) => {
        e.preventDefault();
        if (isActionsDisabled) return;

        if (!validate()) return;

        const payload = {
            unid: selectedProperty?.unid
        };

        try {
            const postData = { name: buildingInput.trim() };
            const res = await createBuildings(payload, postData);

            if (res?.status === 200) {
                const newBuilding = res?.data?.data?.building;

                if (newBuilding?.name) {
                    onBuildingCreated(newBuilding);
                    setIsBuildingDisabled(false)
                    setBuildingInput('');
                    if (typeof onActionSuccess === 'function') {
                        onActionSuccess("Building created successfully", "success");
                    }
                    fetchAllProperty()
                }

            } else {
                console.error('Building not added:', res?.message);
            }
        } catch (error) {
            console.error('Error adding building:', error);
            if (typeof onActionSuccess === 'function') {
                onActionSuccess(error.message, "Failed");
            }
        }
    };

    const handleDeleteBuilding = async (buildingUnid) => {
        if (isActionsDisabled) return;

        const payload = {
            unid: selectedProperty?.unid,
            buildingUnid
        };
        try {
            const res = await deleteBuilding(payload);
            if (res?.status === 200) {
                setBuildings((prev) => prev.filter((b) => b.unid !== buildingUnid));
                setBuildingOptions((prev) => {
                    const deletedBuilding = buildings.find((b) => b.unid === buildingUnid);
                    return prev.filter((name) => name !== deletedBuilding?.name);
                });
                onBuildingCreated();
                fetchAllProperty();
                if (typeof onActionSuccess === 'function') {
                    onActionSuccess("Building deleted successfully", "success");
                }
            } else {
                console.error('Building not deleted:', res?.message);
            }
        } catch (error) {
            console.error('Error deleting building:', error);
            if (typeof onActionSuccess === 'function') {
                onActionSuccess("Failed to delete building, because its in use", "Failed");
            }
        }
    };

    const handleEditBuilding = (unid) => {
        setEditingBuildingUnid(unid);
    };

    const handleBuildingNameChange = (unid, newName) => {
        setBuildings(prev =>
            prev.map(b => (b.unid === unid ? { ...b, name: newName } : b))
        );
    };


    const handleSaveEdit = async (unid) => {
        if (isActionsDisabled) return;
        setEditingBuildingUnid(null);

        const index = buildings.findIndex((x) => x.unid === unid);
        if (index === -1 || !selectedProperty?.unid) return;

        const previousName = buildings[index].name;

        const payload = {
            unid: selectedProperty.unid,
            buildingUnid: unid
        };
        const postdata = { name: buildings[index].name.trim() };

        try {
            const res = await updateBuilding(payload, postdata);
            if (res?.status === 200) {
                const updatedBuilding = res?.data?.data?.building;

                setBuildings((prev) =>
                    prev.map((b) => (b.unid === unid ? updatedBuilding : b))
                );

                setBuildingOptions((prev) =>
                    prev.map((name) => (name === previousName ? updatedBuilding.name : name))
                );
                onBuildingCreated();
                fetchAllProperty()
                if (typeof onActionSuccess === 'function') {
                    onActionSuccess("Building updated successfully", "success");
                }
            }

        } catch (err) {
            console.error('Update failed:', err);
            if (typeof onActionSuccess === 'function') {
                onActionSuccess(err.message, "failed");
                fetchAllProperty()
            }
        }
    };


    const handleCancelEdit = () => {
        setEditingBuildingUnid(null);
        fetchAllProperty()
    }

    return (
        <div className="overlayHolder bg-black/30 dark:bg-black/60 fixed inset-0 flex justify-center z-[999]">
            <div className="modalHlder relative bg-[#F8F8F8] dark:bg-darkThemeModalBg dark:shadow-darkThemeModalShadow rounded-2xl border border-black/10 w-full sm:w-[480px] max-h-[75%]  my-auto p-10 lg:px-8 lg:py-11 overflow-auto mx-5">
                <div className="flex justify-end">
                    <button
                        className="absolute top-5 right-5 text-[#FF8C65] hover:text-[#e06c45] text-2xl font-bold leading-none z-10"
                        onClick={handleBuildingSave}
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" className="*:dark:stroke-white">
                            <path d="M4.7475 20.1575C4.5575 20.1575 4.3675 20.0875 4.2175 19.9375C3.9275 19.6475 3.9275 19.1675 4.2175 18.8775L17.8775 4.2175C18.1675 3.9275 18.6475 3.9275 18.9375 4.2175C19.2275 4.5075 19.2275 4.9875 18.9375 5.2775L5.2775 19.9375C5.1375 20.0875 4.9375 20.1575 4.7475 20.1575Z" fill="#717375" stroke="#717375"></path>
                            <path d="M18.4075 20.1575C18.2175 20.1575 18.0275 20.0875 17.8775 19.9375L4.2175 5.2775C3.9275 4.9875 3.9275 4.5075 4.2175 4.2175C4.5075 3.9275 4.9875 3.9275 5.2775 4.2175L18.9375 18.8775C19.2275 19.1675 19.2275 19.6475 18.9375 19.9375C18.7875 20.0875 18.5975 20.1575 18.4075 20.1575Z" fill="#717375" stroke="#717375"></path>
                        </svg>
                    </button>
                </div>
                <div className="flex gap-10 lg:gap-14">
                    <div className="modalContent basis-full lg:basis-[344px]">
                        <div className="flex flex-col gap-1">
                            <h2 className="modalTitle text-[#031641] dark:text-white text-xl leading-6 lg:text-2xl lg:leading-7 font-semibold tracking-[0.036px]">Add Building</h2>
                            <p className="text-[#000000DE] dark:text-white text-sm leading-5 tracking-[0.021px] font-normal w-full lg:w-11/12">Organize your Gradient units by location.</p>
                        </div>
                        <div className="modalFormHolder max-h-[260px] overflow-y-auto mt-6">
                            <form className="space-y-5 lg:space-y-7">
                                <div className="properySection gap-1 flex flex-col">
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor="propertyName" className="propertyName text-dark-grey dark:text-white font-normal text-base leading-5 tracking-[-0.24px]">Property Name</Label>
                                        <input id="propertyName" type="text" placeholder="Enter Property Name"
                                            className="text-lg leading-6 py-2.5 px-3 rounded-md border border-[#CADBD5] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg dark:placeholder:text-white/40 dark:text-white placeholder:text-dark-grey/40 font-medium tracking-[-0.408px] outline-none w-full flex grow sm:grow-0 sm:w-[272px] pointer-events-none opacity-40"
                                            value={PropertyName}
                                        />
                                    </div>
                                </div>
                                <div className="buildingSection w-full">
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between gap-4">
                                            <Label htmlFor="building" className="text-dark-grey font-normal text-base leading-5 tracking-[-0.24px] flex justify-between w-full grow sm:w-[300px]">
                                                <span className="text-dark-grey max-[420px]:text-sm dark:text-white">Building Name</span>
                                                <span className="text-blue font-medium  max-[420px]:text-sm dark:text-white">Buildings: {buildings?.length}</span>
                                            </Label>
                                            <div className="basis-[60px] shrink-0"></div>
                                        </div>
                                        <div className="flex flex-col gap-6">
                                            <div className="flex items-center gap-4 relative">
                                                <div className="flex items-center gap-2 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg grow sm:basis-[300px]">
                                                    <input
                                                        id="building"
                                                        type="text"
                                                        name='buildingName'
                                                        placeholder="Enter Building"
                                                        className="w-full text-base leading-5 lg:text-lg xl:leading-6 dark:bg-transparent dark:placeholder:text-white/40 dark:text-white  placeholder:text-dark-grey/40 font-medium tracking-[-0.408px] outline-none"
                                                        value={buildingInput}
                                                        onChange={(e) => setBuildingInput(e.target.value)}
                                                    />
                                                </div>
                                                <div className="addIconHolder flex justify-center items-center basis-[60px] shrink-0">
                                                    <button
                                                        className={`text-theme text-base font-semibold leading-6 ${
                                                            isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                                                        }`}
                                                        onClick={handleAddBuilding}
                                                        disabled={isActionsDisabled}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                                {errors.buildingName && (
                                                    <p className='text-critical text-xs leading-4 absolute -bottom-5 left-0'>
                                                        {errors.buildingName}
                                                    </p>
                                                )}
                                            </div>

                                            {buildings.map((item) => (
                                                <div key={item.unid} className="flex items-center gap-4">
                                                    <div className="flex items-center gap-2 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg grow sm:w-[300px]">
                                                        <input
                                                            type="text"
                                                            value={item.name}
                                                            onChange={(e) => handleBuildingNameChange(item.unid, e.target.value)}
                                                            className={`w-full text-base placeholder:text-dark-grey/40 dark:bg-transparent dark:text-white font-medium outline-none ${editingBuildingUnid !== item.unid ? "pointer-events-none opacity-40" : ""}`}
                                                        />
                                                    </div>

                                                    {editingBuildingUnid !== item.unid ? (
                                                        <div className='flex items-center gap-3'>
                                                            <div
                                                                className={`flex justify-center items-center basis-6 shrink-0 ${
                                                                    isActionsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                                                }`}
                                                                onClick={() => {
                                                                    if (isActionsDisabled) return;
                                                                    handleEditBuilding(item.unid);
                                                                }}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                    <path d="M22 10.5V12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2H13.5" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                                                                    <path d="M16.652 3.45506L17.3009 2.80624C18.3759 1.73125 20.1188 1.73125 21.1938 2.80624C22.2687 3.88124 22.2687 5.62415 21.1938 6.69914L20.5449 7.34795M16.652 3.45506C16.652 3.45506 16.7331 4.83379 17.9497 6.05032C19.1662 7.26685 20.5449 7.34795 20.5449 7.34795M16.652 3.45506L10.6872 9.41993C10.2832 9.82394 10.0812 10.0259 9.90743 10.2487C9.70249 10.5114 9.52679 10.7957 9.38344 11.0965C9.26191 11.3515 9.17157 11.6225 8.99089 12.1646L8.41242 13.9M20.5449 7.34795L14.5801 13.3128C14.1761 13.7168 13.9741 13.9188 13.7513 14.0926C13.4886 14.2975 13.2043 14.4732 12.9035 14.6166C12.6485 14.7381 12.3775 14.8284 11.8354 15.0091L10.1 15.5876M10.1 15.5876L8.97709 15.9619C8.71035 16.0508 8.41626 15.9814 8.21744 15.7826C8.01862 15.5837 7.9492 15.2897 8.03811 15.0229L8.41242 13.9M10.1 15.5876L8.41242 13.9" stroke="#FF8C65" strokeWidth="1.5" />
                                                                </svg>
                                                            </div>
                                                            <div
                                                                className={`flex justify-center items-center basis-6 shrink-0 ${
                                                                    isActionsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                                                }`}
                                                                onClick={() => {
                                                                    if (isActionsDisabled) return;
                                                                    handleDeleteBuilding(item.unid);
                                                                }}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                    <path d="M20.5001 6H3.5" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                                                                    <path d="M18.8332 8.5L18.3732 15.3991C18.1962 18.054 18.1077 19.3815 17.2427 20.1907C16.3777 21 15.0473 21 12.3865 21H11.6132C8.95235 21 7.62195 21 6.75694 20.1907C5.89194 19.3815 5.80344 18.054 5.62644 15.3991L5.1665 8.5" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                                                                    <path d="M9.5 11L10 16" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                                                                    <path d="M14.5 11L14 16" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" />
                                                                    <path d="M6.5 6C6.55588 6 6.58382 6 6.60915 5.99936C7.43259 5.97849 8.15902 5.45491 8.43922 4.68032C8.44784 4.65649 8.45667 4.62999 8.47434 4.57697L8.57143 4.28571C8.65431 4.03708 8.69575 3.91276 8.75071 3.8072C8.97001 3.38607 9.37574 3.09364 9.84461 3.01877C9.96213 3 10.0932 3 10.3553 3H13.6447C13.9068 3 14.0379 3 14.1554 3.01877C14.6243 3.09364 15.03 3.38607 15.2493 3.8072C15.3043 3.91276 15.3457 4.03708 15.4286 4.28571L15.5257 4.57697C15.5433 4.62992 15.5522 4.65651 15.5608 4.68032C15.841 5.45491 16.5674 5.97849 17.3909 5.99936C17.4162 6 17.4441 6 17.5 6" stroke="#FF8C65" strokeWidth="1.5" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className='flex items-center gap-3'>
                                                            <div
                                                                className={`flex justify-center items-center basis-6 shrink-0 ${
                                                                    isActionsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                                                }`}
                                                                onClick={() => {
                                                                    if (isActionsDisabled) return;
                                                                    handleSaveEdit(item.unid);
                                                                }}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                    <path d="M2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2C16.714 2 19.0711 2 20.5355 3.46447C22 4.92893 22 7.28595 22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12Z" stroke="#FF8C65" strokeWidth="1.5" />
                                                                    <path d="M8.5 12.5L10.5 14.5L15.5 9.5" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex justify-center items-center basis-6 shrink-0 cursor-pointer" onClick={handleCancelEdit}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                                                                    <path d="M8.99951 9L15.0001 15.0006" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                    <path d="M8.99988 15.0006L15.0005 9" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                    <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="#FF8C65" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
